import React, { createContext, useContext, useState, useCallback } from "react";
import { toast } from "sonner";
import { detectUserStyle } from "../ai/styleEngine";
import { generateThreePreviews } from "../ai/hairstyleTryOnEngine";
import { getSixOccasionOutfits, getAccessoriesAndProducts } from "../ai/outfitCurationEngine";
import { searchProducts } from "../ai/myntraFashionService";
import { generateFinalLook } from "../ai/finalLookEngine";
import { generateLookContent } from "../ai/contentEngine";
import { FALLBACK_ANALYSIS, FALLBACK_CONTENT } from "../data/looks";

const StyleContext = createContext(null);
export const useHOH = () => useContext(StyleContext);

export const STEP = {
  INTRO: 0, OCCASION: 1, UPLOAD: 2, HAIR: 3, OUTFIT: 4,
  ACCESSORIES: 5, FINAL: 6, BOOK: 7, CONTENT: 8, TRENDING: 9,
};

const FALLBACK_OUTFITS = [
  { title: "Classic Formal Set", topwear: "Tailored Shirt", bottomwear: "Slim Trousers", footwear: "Leather Shoes", accessory: "Steel Watch", priceRange: "₹6,000 – ₹11,000", stores: ["Myntra", "AJIO"] },
  { title: "Premium Blazer Look", topwear: "Structured Blazer", bottomwear: "Wool Trousers", footwear: "Suede Loafers", accessory: "Leather Belt", priceRange: "₹12,000 – ₹22,000", stores: ["Tata CLiQ", "Zara"] },
  { title: "Smart Ethnic Set", topwear: "Cotton Kurta", bottomwear: "Churidar", footwear: "Mojaris", accessory: "Pocket Square", priceRange: "₹4,000 – ₹9,000", stores: ["Myntra", "Amazon India"] },
  { title: "Modern Co-ord", topwear: "Textured Overshirt", bottomwear: "Tapered Trousers", footwear: "Minimal Sneakers", accessory: "Sunglasses", priceRange: "₹5,000 – ₹10,000", stores: ["H&M", "AJIO"] },
  { title: "Evening Statement", topwear: "Silk Shirt", bottomwear: "Tailored Trousers", footwear: "Derby Shoes", accessory: "Bracelet", priceRange: "₹7,000 – ₹14,000", stores: ["Nykaa Fashion", "Zara"] },
  { title: "Minimal Luxe", topwear: "Fine Knit Polo", bottomwear: "Pleated Trousers", footwear: "Loafers", accessory: "Perfume", priceRange: "₹8,000 – ₹16,000", stores: ["Tata CLiQ", "Myntra"] },
];

export function StyleProvider({ children }) {
  const [step, setStep] = useState(STEP.INTRO);

  const [occasion, setOccasion] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [styleDirection, setStyleDirection] = useState(null);
  const [needsDirection, setNeedsDirection] = useState(false);
  const [scanning, setScanning] = useState(false);

  const [hairPreviews, setHairPreviews] = useState(null);
  const [loadingHair, setLoadingHair] = useState(false);
  const [selectedHair, setSelectedHair] = useState(null);

  const [outfitOptions, setOutfitOptions] = useState(null);
  const [loadingOutfits, setLoadingOutfits] = useState(false);
  const [selectedOutfit, setSelectedOutfit] = useState(null);
  const [myntraStatus, setMyntraStatus] = useState("idle"); // idle | ok | no_products | sample
  const [isSampleOutfits, setIsSampleOutfits] = useState(false);

  const [accessories, setAccessories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loadingAP, setLoadingAP] = useState(false);
  const [selectedAccessories, setSelectedAccessories] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);

  const [generating, setGenerating] = useState(false);
  const [finalLook, setFinalLook] = useState(null);

  const [bookingConfirmed, setBookingConfirmed] = useState(false);
  const [content, setContent] = useState(null);
  const [generatingContent, setGeneratingContent] = useState(false);

  const goTo = (s) => { setStep(s); window.scrollTo({ top: 0, behavior: "smooth" }); };

  // STEP 3: generate 3 hairstyle previews on the uploaded photo
  const generateHairPreviews = useCallback(async (direction) => {
    setLoadingHair(true);
    setHairPreviews(null);
    try {
      let previews;
      try {
        previews = await generateThreePreviews(photo, direction, occasion);
      } catch (e) {
        previews = [
          { tier: "Classic", name: "Classic", confidence: 93, reason: "Timeless and flattering.", image: photo },
          { tier: "Premium", name: "Premium", confidence: 96, reason: "Elevated and sharp.", image: photo },
          { tier: "Bold", name: "Bold", confidence: 90, reason: "A confident statement.", image: photo },
        ];
        toast.message("Live try-on warming up", { description: "Showing your base photo previews." });
      }
      setHairPreviews(previews.map((p) => ({ ...p, image: p.image || photo })));
    } finally {
      setLoadingHair(false);
    }
  }, [photo, occasion]);

  // STEP 2 -> scan
  const runScan = useCallback(async () => {
    if (!photo) return;
    setScanning(true);
    setHairPreviews(null); setSelectedHair(null); setOutfitOptions(null);
    setSelectedOutfit(null); setFinalLook(null);
    try {
      let res;
      try {
        res = await detectUserStyle(photo, occasion);
      } catch (e) {
        res = { analysis: FALLBACK_ANALYSIS, direction: null };
      }
      setAnalysis(res.analysis);
      // 3s minimum VFX feel
      await new Promise((r) => setTimeout(r, 2600));
      goTo(STEP.HAIR);
      if (res.direction) {
        setStyleDirection(res.direction);
        setNeedsDirection(false);
        generateHairPreviews(res.direction);
      } else {
        setNeedsDirection(true);
      }
    } finally {
      setScanning(false);
    }
  }, [photo, occasion, generateHairPreviews]);

  const chooseDirection = useCallback((dir) => {
    setStyleDirection(dir);
    setNeedsDirection(false);
    generateHairPreviews(dir);
  }, [generateHairPreviews]);

  // STEP 4: search REAL Myntra products
  const loadOutfits = useCallback(async () => {
    setLoadingOutfits(true);
    setOutfitOptions(null);
    setSelectedOutfit(null);
    setIsSampleOutfits(false);
    try {
      let res;
      try {
        res = await searchProducts(occasion, styleDirection || "androgynous", selectedHair);
      } catch (e) {
        res = { status: "no_products", products: [] };
      }
      if (res.status === "ok" && res.products?.length) {
        setOutfitOptions(res.products);
        setMyntraStatus("ok");
      } else {
        setOutfitOptions([]);
        setMyntraStatus("no_products");
      }
    } finally {
      setLoadingOutfits(false);
    }
  }, [occasion, styleDirection, selectedHair]);

  // Optional preview with AI sample looks (clearly labelled, not real products)
  const loadSampleOutfits = useCallback(async () => {
    setLoadingOutfits(true);
    try {
      let options;
      try {
        options = await getSixOccasionOutfits(occasion, styleDirection || "androgynous", selectedHair);
      } catch (e) {
        options = FALLBACK_OUTFITS;
      }
      setOutfitOptions((options || []).map((o) => ({
        title: o.title, brand: "HOH Sample", price: o.priceRange, rating: null, image: null, url: null,
        isSample: true, topwear: o.topwear, bottomwear: o.bottomwear, footwear: o.footwear, accessory: o.accessory,
      })));
      setIsSampleOutfits(true);
      setMyntraStatus("sample");
    } finally {
      setLoadingOutfits(false);
    }
  }, [occasion, styleDirection, selectedHair]);

  // STEP 5: accessories + products
  const loadAccessoriesProducts = useCallback(async () => {
    setLoadingAP(true);
    try {
      let data;
      try {
        data = await getAccessoriesAndProducts(occasion, styleDirection || "androgynous", selectedOutfit, analysis?.beardStatus);
      } catch (e) {
        data = { accessories: [], products: [] };
      }
      setAccessories(data.accessories || []);
      setProducts(data.products || []);
    } finally {
      setLoadingAP(false);
    }
  }, [occasion, styleDirection, selectedOutfit, analysis]);

  const toggleAccessory = (item) => setSelectedAccessories((p) => p.find((i) => i.name === item.name) ? p.filter((i) => i.name !== item.name) : [...p, item]);
  const toggleProduct = (item) => setSelectedProducts((p) => p.find((i) => i.name === item.name) ? p.filter((i) => i.name !== item.name) : [...p, item]);

  // STEP 6: final look
  const runGenerateLook = useCallback(async () => {
    if (!photo || !selectedOutfit) return;
    setGenerating(true);
    setFinalLook(null);
    try {
      const isSample = selectedOutfit.isSample;
      const outfit = isSample
        ? [
            { name: selectedOutfit.topwear, category: "Top Wear" },
            { name: selectedOutfit.bottomwear, category: "Bottom Wear" },
            { name: selectedOutfit.footwear, category: "Footwear" },
          ]
        : [{ name: selectedOutfit.title, category: "Outfit" }];
      const accs = [
        ...(selectedOutfit.accessory ? [{ name: selectedOutfit.accessory, category: "Accessory" }] : []),
        ...selectedAccessories.map((a) => ({ name: a.name, category: a.category })),
      ];
      let image;
      try {
        const res = await generateFinalLook({
          userPhoto: photo,
          occasion,
          styleDirection: styleDirection || "androgynous",
          selectedHairstyle: selectedHair,
          selectedOutfit: outfit,
          selectedAccessories: accs,
          selectedProducts,
          productImageUrl: isSample ? undefined : selectedOutfit.image,
          productTitle: isSample ? undefined : selectedOutfit.title,
        });
        image = res.image;
      } catch (e) {
        image = selectedHair?.image || photo;
        toast.message("Preview ready", { description: "Live image AI warming up — showing your styled base." });
      }
      setFinalLook({
        image,
        title: selectedOutfit.title,
        occasion,
        styleDirection: styleDirection || "androgynous",
        hairstyle: selectedHair?.name,
        outfit: selectedOutfit.title,
        productUrl: selectedOutfit.url,
        accessories: [selectedOutfit.accessory, ...selectedAccessories.map((a) => a.name)].filter(Boolean),
        products: selectedProducts.map((p) => p.name),
        confidence: selectedHair?.confidence || analysis?.confidence || 94,
      });
    } finally {
      setGenerating(false);
    }
  }, [photo, occasion, styleDirection, selectedHair, selectedOutfit, selectedAccessories, selectedProducts, analysis]);

  const confirmBooking = () => { setBookingConfirmed(true); };

  const runGenerateContent = useCallback(async () => {
    setGeneratingContent(true);
    try {
      let c;
      try {
        c = await generateLookContent(finalLook || { occasion, hairstyle: selectedHair?.name });
      } catch (e) {
        c = FALLBACK_CONTENT;
      }
      setContent(c);
    } finally {
      setGeneratingContent(false);
    }
  }, [finalLook, occasion, selectedHair]);

  const backToLanding = () => {
    setStep(STEP.INTRO);
    setOccasion(null); setPhoto(null); setAnalysis(null); setStyleDirection(null); setNeedsDirection(false);
    setHairPreviews(null); setSelectedHair(null); setOutfitOptions(null); setSelectedOutfit(null);
    setMyntraStatus("idle"); setIsSampleOutfits(false);
    setAccessories([]); setProducts([]); setSelectedAccessories([]); setSelectedProducts([]);
    setFinalLook(null); setBookingConfirmed(false); setContent(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const restartFromOccasion = (occ) => {
    setOccasion(occ || null);
    setPhoto(null); setAnalysis(null); setStyleDirection(null); setNeedsDirection(false);
    setHairPreviews(null); setSelectedHair(null); setOutfitOptions(null); setSelectedOutfit(null);
    setAccessories([]); setProducts([]); setSelectedAccessories([]); setSelectedProducts([]);
    setFinalLook(null); setBookingConfirmed(false); setContent(null);
    goTo(STEP.UPLOAD);
    toast.success("Recreating look — upload your photo to begin");
  };

  const value = {
    step, setStep, goTo,
    occasion, setOccasion,
    photo, setPhoto,
    analysis, styleDirection, needsDirection, scanning, runScan, chooseDirection,
    hairPreviews, loadingHair, selectedHair, setSelectedHair, generateHairPreviews,
    outfitOptions, loadingOutfits, loadOutfits, loadSampleOutfits, myntraStatus, isSampleOutfits, selectedOutfit, setSelectedOutfit,
    accessories, products, loadingAP, loadAccessoriesProducts,
    selectedAccessories, toggleAccessory, selectedProducts, toggleProduct,
    generating, finalLook, runGenerateLook,
    bookingConfirmed, confirmBooking,
    content, generatingContent, runGenerateContent,
    restartFromOccasion,
    backToLanding,
  };

  return <StyleContext.Provider value={value}>{children}</StyleContext.Provider>;
}
